import { useState } from 'react';
import { emailValidationService } from '@/services/emailValidationService';

type ValidationStatus = 'idle' | 'checking' | 'valid' | 'invalid' | 'wrong-role';

interface ValidationState {
  status: ValidationStatus;
  message: string;
  existingUserType?: 'seller' | 'buyer';
}

/**
 * Custom hook for email validation with role detection
 * @param currentUserType - The type of user currently registering ('seller' or 'buyer')
 */
export const useEmailValidation = (currentUserType: 'seller' | 'buyer') => {
  const [validationState, setValidationState] = useState<ValidationState>({
    status: 'idle',
    message: ''
  });

  const validateEmail = async (email: string) => {
    if (!email) {
      setValidationState({ status: 'idle', message: '' });
      return;
    }

    setValidationState({ status: 'checking', message: '' });

    const result = await emailValidationService.checkEmailExists(email);

    if (result.error) {
      setValidationState({
        status: 'invalid',
        message: result.error
      });
      return;
    }

    if (result.exists) {
      // Check if the existing account is the opposite role
      const oppositeRole = currentUserType === 'seller' ? 'buyer' : 'seller';
      
      if (result.userType === oppositeRole) {
        setValidationState({
          status: 'wrong-role',
          message: '',
          existingUserType: result.userType
        });
      } else {
        setValidationState({
          status: 'invalid',
          message: `This email is already registered as a ${currentUserType}`
        });
      }
    } else {
      setValidationState({
        status: 'valid',
        message: ''
      });
    }
  };

  const getValidationMessageData = () => {
    if (validationState.status === 'wrong-role' && validationState.existingUserType) {
      const oppositeRole = validationState.existingUserType;
      const loginPath = oppositeRole === 'buyer' ? '/buyer/login' : '/login';
      const roleName = oppositeRole === 'buyer' ? 'buyer' : 'seller';
      const currentRoleName = currentUserType === 'buyer' ? 'buyer' : 'seller';

      return {
        type: 'wrong-role' as const,
        roleName,
        loginPath,
        currentRoleName
      };
    }

    if (validationState.status === 'invalid' && validationState.message) {
      return {
        type: 'invalid' as const,
        message: validationState.message
      };
    }

    return null;
  };

  return {
    validationState,
    validateEmail,
    getValidationMessageData,
    isValid: validationState.status === 'valid'
  };
};
